import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AmountInput from "@/components/AmountInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, ArrowDownCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

/**
 * Expense section visible only on the agency sheet when consenti_spese is on.
 * Lets the agente record outflows (paid invoices, expenses from cash, etc.).
 */
export default function ExpensesSection({ sheetId, expenses = [], canEdit = true, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contraente: "", importo: "", note: "" });
  const [saving, setSaving] = useState(false);

  const total = expenses.reduce((s, e) => s + Number(e.importo || 0), 0);

  const submit = async () => {
    const importo = parseFloat(String(form.importo).replace(",", "."));
    if (!form.contraente.trim()) {
      toast.error("Indica a chi sono andati i soldi");
      return;
    }
    if (!isFinite(importo) || importo <= 0) {
      toast.error("Importo deve essere maggiore di zero");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/cash-sheets/${sheetId}/expenses`, {
        contraente: form.contraente.trim(),
        importo,
        note: form.note || "",
      });
      toast.success("Uscita registrata");
      setForm({ contraente: "", importo: "", note: "" });
      setOpen(false);
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Elimina questa uscita di cassa?")) return;
    try {
      await api.delete(`/cash-sheets/${sheetId}/expenses/${id}`);
      toast.success("Uscita eliminata");
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Card className="border-red-100 bg-red-50/30 p-3" data-testid="expenses-section">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-red-100 text-red-700 flex items-center justify-center">
            <ArrowDownCircle className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold leading-tight">Uscite di cassa</div>
            <div className="font-heading text-sm font-semibold text-slate-900 leading-tight">Spese e pagamenti</div>
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid="add-expense-btn">
                <Plus className="w-3 h-3 mr-1" /> Aggiungi uscita
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Registra uscita di cassa</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">A chi sono andati i soldi *</label>
                  <Input
                    placeholder="Es. Telecom Italia, fornitore, dipendente…"
                    value={form.contraente}
                    onChange={(e) => setForm({ ...form, contraente: e.target.value })}
                    data-testid="expense-contraente"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Importo dell'uscita (€) *</label>
                  <AmountInput
                    placeholder="0,00"
                    value={form.importo}
                    onChange={(e) => setForm({ ...form, importo: e.target.value })}
                    data-testid="expense-importo"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Note (facoltative)</label>
                  <Input
                    placeholder="Es. fattura 234/2025, spesa ufficio…"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    data-testid="expense-note"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="expense-save">
                  {saving ? "Salvataggio…" : "Registra uscita"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {expenses.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-3">
          Nessuna uscita registrata oggi.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left bg-white/60 border-y border-red-100">
                <th className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Beneficiario</th>
                <th className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Note</th>
                <th className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-right">Importo</th>
                {canEdit && <th className="px-2 py-1.5 w-10"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-red-100">
              {expenses.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`}>
                  <td className="px-2 py-1.5 font-medium text-slate-900">{e.contraente}</td>
                  <td className="px-2 py-1.5 text-slate-600">{e.note || <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num font-semibold text-red-700">−{formatEUR(e.importo)}</td>
                  {canEdit && (
                    <td className="px-2 py-1.5 text-right">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(e.id)} data-testid={`expense-delete-${e.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-red-200">
                <td colSpan={2} className="px-2 py-1.5 text-right text-xs font-semibold text-slate-700">Totale uscite del giorno</td>
                <td className="px-2 py-1.5 text-right font-mono-num font-bold text-red-700 text-sm" data-testid="expenses-total">−{formatEUR(total)}</td>
                {canEdit && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}
